/**
 * A condition node in words (Feature 79, revision 1): its kind as a heading
 * says it, what it tests a line each, and the steps it runs. Shared by the
 * gate's inspector, the condition's, and their cards.
 */
import type { LinkageDecision } from '@optum/api-client/types';

import type { Condition } from './conditions.js';
import type { Draft } from './draft.js';
import { conditionWords } from './gate-lines.js';

/** A condition's kind as a heading says it: `If`, `Else if`, `Case`. */
export function kindLabel(condition: Pick<Condition, 'kind'>): string {
  return condition.kind.charAt(0).toUpperCase() + condition.kind.slice(1);
}

/** The steps a gate's branch links to, or that it has none yet. */
export function branchSteps(draft: Draft, gate: string, branch: string): string[] {
  return draft.links.filter((l) => l.from === gate && l.branch === branch).map((l) => l.to);
}

/** Where a branch leads: `→ suggest`, or how to give it a step. */
export function stepsOf(draft: Draft, gate: string, branch: string): string {
  const to = branchSteps(draft, gate, branch);
  return to.length > 0 ? `→ ${to.join(', ')}` : '→ drag from its node to a step';
}

/** What a branch tests, a line each: `when …`, `and …`; a case's values. */
export function testLines(decision: LinkageDecision, branch: string): string[] {
  const found = decision.branches.find((b) => b.name === branch);
  if (!found) return [];
  if (decision.mode === 'switch') {
    const values = found.values ?? [];
    return [values.length > 0 ? `values ${values.join(', ')}` : 'no value yet'];
  }
  const rules = found.rules ?? { join: 'all', items: [] };
  const join = rules.join === 'any' ? 'or' : 'and';
  const words = conditionWords(rules);
  return words.length > 0
    ? words.map((w, i) => `${i === 0 ? 'when' : join} ${w}`)
    : ['no condition yet'];
}
